// useWebView.ts
import { useCallback } from 'react';
import useIsIOS from "@hooks/useIsIOS";

// Define the interface for WebAppInterface

// Extend the Window interface to include WebAppInterface

/**
 * Custom hook to interact with the Android WebAppInterface with Promise-based functions.
 *
 * @returns An object containing methods to interact with WebAppInterface.
 */

var _useAndroidWebview = function _useAndroidWebview() {
  /**
   * A reusable function to call a WebAppInterface method.
   *
   * @param methodName - The name of the WebAppInterface method to call.
   * @param args - Arguments to pass to the method.
   * @returns {Promise<void>}
   */
  var callWebAppInterface = useCallback(function (methodName) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return new Promise(function (resolve, reject) {
      if (typeof window !== 'undefined' && window.Android && typeof window.Android[methodName] === 'function') {
        try {
          var _window$Android;
          (_window$Android = window.Android)[methodName].apply(_window$Android, args);
          resolve();
        } catch (error) {
          console.error("Error executing ".concat(methodName, ":"), error);
          reject(error);
        }
      } else {
        var error = new Error("Android.".concat(methodName, " is not available."));
        console.warn(error.message);
        reject(error);
      }
    });
  }, []);

  /**
   * Opens a file from a Base64 string.
   *
   * @param base64String - The Base64 encoded string of the file.
   * @param originalFileName - The original name of the file.
   * @param mimeType - The MIME type of the file.
   * @returns {Promise<void>}
   */
  var openBase64File = useCallback(function (base64String, originalFileName, mimeType) {
    return callWebAppInterface('openBase64File', base64String, originalFileName, mimeType);
  }, [callWebAppInterface]);

  /**
   * Opens a file from a URL.
   *
   * @param fileUrl - The URL of the file to open.
   * @returns {Promise<void>}
   */
  var openFile = useCallback(function (fileUrl) {
    return callWebAppInterface('openFile', fileUrl);
  }, [callWebAppInterface]);

  /**
   * Shows a toast message.
   *
   * @param message - The message to display in the toast.
   * @returns {Promise<void>}
   */
  var showToast = useCallback(function (message) {
    return callWebAppInterface('showToast', message);
  }, [callWebAppInterface]);

  /**
   * Closes the current screen.
   *
   * @returns {Promise<void>}
   */
  var closeScreen = useCallback(function () {
    return callWebAppInterface('closeScreen');
  }, [callWebAppInterface]);

  /**
   * Shows or hides the title bar.
   * @param isVisible - A boolean value to show or hide the title bar.
   * @returns {Promise<void>}
   */
  var showTitleBar = useCallback(function (isVisible) {
    return callWebAppInterface('showTitleBar', isVisible);
  }, [callWebAppInterface]);

  /**
   * OpenUri screen.
   * @returns {Promise<void>}
   */
  var openUri = useCallback(function (uri) {
    return callWebAppInterface('openUri', uri);
  }, [callWebAppInterface]);

  /**
   * OpenUri screen.
   * @returns {Promise<void>}
   */
  var logger = useCallback(function (tag, message) {
    return callWebAppInterface('logger', tag, message);
  }, [callWebAppInterface]);

  /**
   * Open a screen with the given screen name and data.
   * @param screenName - The name of the screen to open.
   * @param data - Data to pass to the screen.
   * @returns {Promise<void>}
   */
  var openScreen = useCallback(function (screenName, data) {
    return callWebAppInterface('openScreen', screenName, JSON.stringify(data));
  }, [callWebAppInterface]);

  /**
   * Track an event with the given name and payload.
   * @param eventName - The name of the event to track.
   * @param payload - Data associated with the event.
   * @returns {Promise<void>}
   */
  var trackEvent = useCallback(function (eventName, payload) {
    return callWebAppInterface('trackEvent', eventName, JSON.stringify(payload));
  }, [callWebAppInterface]);

  /**
   * Set the screen to return to when the back button is pressed.
   * @param screenName - The name of the screen to return to.
   * @returns {Promise<void>}
   */
  var setBackButtonScreen = useCallback(function (screenName) {
    return callWebAppInterface('setBackButtonScreen', screenName);
  }, [callWebAppInterface]);
  /**
   * Re-authenticate the user.
   */
  var reAuth = useCallback(function () {
    return callWebAppInterface('reAuth');
  }, [callWebAppInterface]);

  /**
   * Set user info.
   * @param userInfo - The user info to set.
   * @returns {Promise<void>}
   */
  var setUserInfo = useCallback(function (userInfo) {
    return callWebAppInterface('setUserInfo', userInfo);
  }, [callWebAppInterface]);

  /**
   * hide bottom nav android
   * @param isVisible - A boolean value to show or hide the bottom nav.
   * @return {Promise<void>}
   * @method showBottomBar
   */

  var showBottomBar = useCallback(function (isVisible) {
    return callWebAppInterface('showBottomBar', {
      isVisible: isVisible
    });
  }, [callWebAppInterface]);

  /**
   * show loading android
   * @param isVisible - A boolean value to show or hide the loading.
   * @return {Promise<void>}
   * @method showLoading
   */
  var showLoading = useCallback(function (isVisible) {
    return callWebAppInterface('showLoading', {
      isVisible: isVisible
    });
  }, [callWebAppInterface]);

  /**
   * send deep link to the mobile application to open a screen.
   * this function differs from method openUri which responsible for opening uri like maps:// or tel://
   * @param deeplink - The deep link to open.
   * @return {Promise<void>}
   */
  var openDeepLink = useCallback(function (deeplink) {
    return callWebAppInterface('openDeepLink', deeplink);
  }, [callWebAppInterface]);
  /**
   * Request location permission from the user.
   * @returns {Promise<void>}
   * @method askLocationPermission
   */
  var askLocationPermission = useCallback(function () {
    return callWebAppInterface('askLocationPermission');
  }, [callWebAppInterface]);
  var onCartPaymentSuccess = useCallback(function () {
    return callWebAppInterface('onCartPaymentSuccess');
  }, [callWebAppInterface]);
  return {
    openBase64File: openBase64File,
    openFile: openFile,
    showToast: showToast,
    closeScreen: closeScreen,
    showTitleBar: showTitleBar,
    openUri: openUri,
    logger: logger,
    openScreen: openScreen,
    trackEvent: trackEvent,
    setBackButtonScreen: setBackButtonScreen,
    reAuth: reAuth,
    setUserInfo: setUserInfo,
    showBottomBar: showBottomBar,
    showLoading: showLoading,
    openDeepLink: openDeepLink,
    askLocationPermission: askLocationPermission,
    onCartPaymentSuccess: onCartPaymentSuccess
  };
};

/**
 * Custom hook to interact with the iOS WebAppInterface with Promise-based functions.
 *
 * @returns An object containing methods to interact with WebAppInterface.
 */
var _useIosWebview = function _useIosWebview() {
  /**
   * A reusable function to call an iOS WebAppInterface method.
   *
   * @param handlerName - The name of the message handler to call.
   * @param data - Data to pass to the handler.
   * @returns {Promise<void>}
   */
  var callIosWebAppInterface = useCallback(function (handlerName, data) {
    console.log("handlerName", handlerName, "data", data);
    return new Promise(function (resolve, reject) {
      if (typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[handlerName]) {
        try {
          window.webkit.messageHandlers[handlerName].postMessage(data);
          resolve();
        } catch (error) {
          console.error("Error executing ".concat(handlerName, ":"), error);
          reject(error);
        }
      } else {
        var error = new Error("iOS handler ".concat(handlerName, " is not available."));
        console.warn(error.message);
        reject(error);
      }
    });
  }, []);
  var openBase64File = useCallback(function (base64String, originalFileName, mimeType) {
    return callIosWebAppInterface('openBase64File', {
      base64String: base64String,
      originalFileName: originalFileName,
      mimeType: mimeType
    });
  }, [callIosWebAppInterface]);
  var openFile = useCallback(function (fileUrl) {
    return callIosWebAppInterface('openFile', fileUrl);
  }, [callIosWebAppInterface]);
  var showToast = useCallback(function (message) {
    return callIosWebAppInterface('showToast', message);
  }, [callIosWebAppInterface]);
  var closeScreen = useCallback(function () {
    return callIosWebAppInterface('closeScreen', {});
  }, [callIosWebAppInterface]);
  var showTitleBar = useCallback(function (isVisible) {
    return callIosWebAppInterface('showTitleBar', {
      isVisible: isVisible
    });
  }, [callIosWebAppInterface]);
  var openUri = useCallback(function (uri) {
    return callIosWebAppInterface('openUri', {
      uri: uri
    });
  }, [callIosWebAppInterface]);
  var logger = useCallback(function (tag, message) {
    return callIosWebAppInterface('logger', {
      tag: tag,
      message: message
    });
  }, [callIosWebAppInterface]);

  /**
   * Open a screen with the given screen name and data.
   * @param screenName - The name of the screen to open.
   * @param data - Data to pass to the screen.
   * @returns {Promise<void>}
   */
  var openScreen = useCallback(function (screenName, data) {
    return callIosWebAppInterface('openScreen', {
      screenName: screenName,
      data: JSON.stringify(data)
    });
  }, [callIosWebAppInterface]);

  /**
   * Track an event with the given name and payload.
   * @param eventName - The name of the event to track.
   * @param payload - Data associated with the event.
   * @returns {Promise<void>}
   */
  var trackEvent = useCallback(function (eventName, payload) {
    return callIosWebAppInterface('trackEvent', {
      eventName: eventName,
      payload: JSON.stringify(payload)
    });
  }, [callIosWebAppInterface]);

  /**
   * Set the screen to return to when the back button is pressed.
   * @param screenName - The name of the screen to return to.
   * @returns {Promise<void>}
   */
  var setBackButtonScreen = useCallback(function (screenName) {
    return callIosWebAppInterface('', {
      screenName: screenName
    });
  }, [callIosWebAppInterface]);
  /**
   * Re-authenticate the user.
   */
  var reAuth = useCallback(function () {
    return callIosWebAppInterface('', {});
  }, [callIosWebAppInterface]);

  /**
   * Set user info.
   * @param userInfo - The user info to set.
   * @returns {Promise<void>}
   */
  var setUserInfo = useCallback(function (userInfo) {
    return callIosWebAppInterface('setUserInfo', {
      userInfo: userInfo
    });
  }, [callIosWebAppInterface]);
  /**
   * hide bottom nav
   * @param isVisible - A boolean value to show or hide the bottom nav.
   * @return {Promise<void>}
   * @method showBottomBar
   */
  var showBottomBar = useCallback(function (isVisible) {
    return callIosWebAppInterface('showBottomBar', {
      isVisible: isVisible
    });
  }, [callIosWebAppInterface]);
  /**
   * show loading
   * @param isVisible - A boolean value to show or hide the loading.
   * @return {Promise<void>}
   *  @method showLoading
   */

  var showLoading = useCallback(function (isVisible) {
    return callIosWebAppInterface('showLoading', {
      isVisible: isVisible
    });
  }, [callIosWebAppInterface]);

  /**
   * send deep link to the mobile application to open a screen.
   * this function differs from method openUri which responsible for opening uri like maps:// or tel://
   * @param deeplink - The deep link to open.
   * @return {Promise<void>}
   */
  var openDeepLink = useCallback(function (deeplink) {
    return callIosWebAppInterface('openDeepLink', {
      deeplink: deeplink
    });
  }, [callIosWebAppInterface]);
  /**
   * Request location permission from the user.
   * @returns {Promise<void>}
   * @method askLocationPermission
   */
  var askLocationPermission = useCallback(function () {
    return callIosWebAppInterface('askLocationPermission');
  }, [callIosWebAppInterface]);
  var onCartPaymentSuccess = useCallback(function () {
    return callIosWebAppInterface('onCartPaymentSuccess');
  }, [callIosWebAppInterface]);
  return {
    openBase64File: openBase64File,
    openFile: openFile,
    showToast: showToast,
    closeScreen: closeScreen,
    showTitleBar: showTitleBar,
    openUri: openUri,
    logger: logger,
    openScreen: openScreen,
    trackEvent: trackEvent,
    setBackButtonScreen: setBackButtonScreen,
    reAuth: reAuth,
    setUserInfo: setUserInfo,
    showBottomBar: showBottomBar,
    showLoading: showLoading,
    openDeepLink: openDeepLink,
    askLocationPermission: askLocationPermission,
    onCartPaymentSuccess: onCartPaymentSuccess
  };
};
var useWebView = function useWebView() {
  var is_ios = useIsIOS();
  if (is_ios) {
    return _useIosWebview();
  }
  return _useAndroidWebview();
};
export default useWebView;